import React, { useEffect, useRef } from 'react';

/**
 * window.$ is jQuery loaded globally via a script tag in `public/index.html`.
 * Since `$` is already declared as `any` elsewhere, we cast at usage rather
 * than re-declaring the Window interface to avoid conflicts.
 */

interface JQueryObject {
  datepicker: (options: { format: string }) => JQueryObject;
  on: (event: string, handler: (event: unknown) => void) => JQueryObject;
}

interface DatePickerProps {
  id: string;
  name: string;
  value: string;
  onChange: (event: unknown) => void;
  inputClassNames?: string;
  width?: string;
}

/**
 * A legacy date picker that wraps a text input with the jQuery bootstrap-datepicker plugin.
 * The plugin is initialized via `window.$` (loaded globally).
 */
const DatePicker = ({ id, name, value, onChange, inputClassNames, width = '100px' }: DatePickerProps) => {
  // Use a ref to always access the latest onChange without re-running the effect.
  // bootstrap-datepicker's .on('changeDate') stacks handlers, so we must only bind once (on mount).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const $ = (window as { $: (selector: string) => JQueryObject }).$;
    $('#' + id).datepicker({ format: 'm/d/yyyy' }).on('changeDate', (event: unknown) => {
      onChangeRef.current(event);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <input
      type="text"
      className={inputClassNames}
      style={{ display: 'inline', width, marginRight: '0' }}
      data-date-autoclose="true"
      id={id}
      name={name}
      value={value}
      onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
      placeholder="MM/DD/YYYY"
      readOnly
    />
  );
};

export default DatePicker;
